import React from 'react';
import { Home, Building2, MessageSquare, Sparkles } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { servicesData, whatsappMessages, contactData } from '../utils/mock';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const iconMap = {
  Home: Home,
  Building2: Building2,
  MessageSquare: MessageSquare,
  Sparkles: Sparkles
};

const serviceMessages = {
  1: whatsappMessages.residential,
  2: whatsappMessages.commercial,
  3: whatsappMessages.consultation,
  4: whatsappMessages.general
};

export const Services = () => {
  const [titleRef, titleVisible] = useScrollAnimation({ threshold: 0.3, once: true });
  const [cardsRef, cardsVisible] = useScrollAnimation({ threshold: 0.1, once: true });

  const handleWhatsAppClick = (serviceId) => {
    const phoneNumber = contactData.phone.replace(/\s+/g, '');
    const message = serviceMessages[serviceId] || whatsappMessages.general;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section id="services" className="py-20 bg-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-10 left-0 w-64 h-64 bg-rose-100/40 rounded-full blur-3xl"></div>
      <div className="absolute bottom-10 right-0 w-80 h-80 bg-pink-100/40 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div
          ref={titleRef}
          className={`text-center mb-16 transition-all duration-1000 ${
            titleVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Our Services
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Comprehensive interior design solutions tailored to your unique needs and vision
          </p>
        </div>

        {/* Services Grid */}
        <div ref={cardsRef} className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {servicesData.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card
                key={service.id}
                className={`group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-700 bg-white rounded-2xl cursor-pointer ${
                  cardsVisible 
                    ? 'opacity-100 translate-x-0' 
                    : index % 2 === 0 
                      ? 'opacity-0 -translate-x-20' 
                      : 'opacity-0 translate-x-20'
                }`}
                style={{ 
                  transitionDelay: cardsVisible ? `${index * 150}ms` : '0ms'
                }}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 group-hover:rotate-2"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 transform group-hover:scale-110 transition-transform duration-300">
                    <div className="bg-gradient-to-r from-rose-500 to-pink-500 rounded-full p-3 shadow-lg">
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-rose-600 transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <Button
                    onClick={() => handleWhatsAppClick(service.id)}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Inquire on WhatsApp
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
